import { ClickAwayListener } from '@mui/material';
import { ExpandableBox, Textarea } from '@crono-one/design-system';
import { StrategyStep } from 'crono-fe-common/types/strategyStep';
import { useState } from 'react';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';

interface ITodoSequenceTaskFormProps {
  step: StrategyStep | TaskTodo;
  onSubjectChange: (subject: string) => void;
  onDescriptionChange: (description: string) => void;
}

export const TodoSequenceTaskForm = ({
  step,
  onSubjectChange,
  onDescriptionChange,
}: ITodoSequenceTaskFormProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(true);
  const stepValues = {
    subject: step.subject ?? '',
    description: step.description ?? '',
  };
  const [draft, setDraft] = useState(stepValues);
  const [syncedStep, setSyncedStep] = useState(stepValues);

  // Adjusting state during render (react.dev "You Might Not Need an Effect"):
  // pick up changes made outside this form, e.g. a refetch after another user
  // edited the step, without overwriting a draft that is being edited.
  if (
    stepValues.subject !== syncedStep.subject ||
    stepValues.description !== syncedStep.description
  ) {
    setSyncedStep(stepValues);
    if (!isEditMode) setDraft(stepValues);
  }

  const handleSave = (e: MouseEvent | TouchEvent) => {
    const subjectChanged = draft.subject !== (step.subject ?? '');
    const descriptionChanged = draft.description !== (step.description ?? '');

    if (isEditMode && (subjectChanged || descriptionChanged)) {
      e.preventDefault();
      e.stopPropagation();
      if (subjectChanged) onSubjectChange(draft.subject);
      if (descriptionChanged) onDescriptionChange(draft.description);
    }
    setIsEditMode(false);
  };

  return (
    <ClickAwayListener
      onClickAway={handleSave}
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
    >
      <div
        className="flex-1 flex flex-col w-full gap-2"
        onFocus={() => {
          if (isEditMode) return;
          setIsEditMode(true);
          setExpanded(true);
        }}
      >
        {/* Subject/Title Field */}
        <div className="flex items-center gap-2 px-4 py-2.5">
          <label htmlFor="title" className="text-sm text-gray-700">
            Title:
          </label>

          <input
            id="title"
            className="flex-1 font-sans border-none outline-none overflow-hidden text-sm p-0"
            value={draft.subject}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          />
        </div>

        {/* Description Field */}
        <ExpandableBox
          className="border-neutral-300 text-xs leading-4.5"
          expanded={expanded}
          onExpandedChange={setExpanded}
        >
          <Textarea
            variant="text"
            rows={1}
            className="block min-h-0 resize-none py-0 text-xs leading-4.5"
            placeholder="Insert description..."
            value={draft.description}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
          />
        </ExpandableBox>
      </div>
    </ClickAwayListener>
  );
};
